/**
 * IcMemoPage — the IC Memo on its own page, at /<deal-name>-ic-memo.
 *
 * The deal page's IC Memo button opens this in a new tab: a link that can be
 * shared, and a page with nothing on it but the memo - no rail, no pipeline,
 * no deal room.
 *
 * Two ways in (DM, 2026-11-02):
 *   - signed in to Proxy: straight in, full edit access, and a Share panel to
 *     set the memo's password and copy its link;
 *   - anyone else: PublicIcMemo asks for the deal's password (lib/memoShare),
 *     then shows the memo read-only with comments open.
 *
 * The version picker writes ?v=<n> into the address bar, so a copied link
 * opens the version that was on screen. No ?v = the deal's current version.
 */

import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Share2, Lock, Copy, Check } from "lucide-react";
import { supabase, loadUwVersions, dbToDeal, fetchAllRows } from "../../supabase";
import { findMemoDeal, dealTitle } from "../../utils/memoLink";
import { UwVersionSelect } from "../pipeline/DealRoom";
import PPLogo from "../common/PPLogo";
import { loginRequest } from "../../auth/msalConfig";
import { loadShare, setSharePassword, clearShare, checkPassword, isUnlocked, rememberUnlock } from "./lib/memoShare";

const IcMemoTab = lazy(() => import("./IcMemoTab"));

const note = (text) => (
  <div className="max-w-7xl mx-auto px-6 py-16 text-[#8a8a8a] text-sm">{text}</div>
);

function PageFrame({ title, right, children }) {
  return (
    <div className="min-h-screen bg-[#1c1c1c] text-[#f4f4f4] font-montserrat flex flex-col">
      <header className="border-b border-[#303030] bg-[#1c1c1c]">
        <div className="max-w-7xl mx-auto w-full px-6 py-3 flex items-center gap-4">
          <a href="/" title="Open Proxy" className="flex-shrink-0"><PPLogo height={22} variant="white" /></a>
          <span className="text-[#5a5a5a]">|</span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#e93529] font-semibold">IC Memo</div>
            <div className="text-[15px] font-semibold truncate">{title}</div>
          </div>
          {right}
        </div>
      </header>
      <main className="flex-1 w-full min-w-0">{children}</main>
    </div>
  );
}

// Signed-in users: set or change the memo's password, copy the link.
function SharePanel({ deal, userName }) {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(undefined);
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (open) loadShare(deal.id).then(setShare); }, [open, deal.id]);

  const save = async () => {
    if (pw.length < 8) { setMsg("Use at least 8 characters."); return; }
    setMsg("Saving…");
    const err = await setSharePassword(deal.id, pw, userName);
    if (err) { setMsg(`Could not save: ${err}`); return; }
    setPw(""); setMsg("Password saved. Send it separately from the link."); setShare(await loadShare(deal.id));
  };
  const remove = async () => {
    if (!window.confirm("Remove the password? The link will stop working for anyone outside Proxy.")) return;
    const err = await clearShare(deal.id);
    setMsg(err ? `Could not remove: ${err}` : "Password removed - the memo is no longer shared."); setShare(null);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
  };

  return (
    <div className="relative flex-shrink-0">
      <button onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold border border-[#3a3a3a] bg-[#242424] text-[#d8d8d8] hover:text-[#f4f4f4]">
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] z-50 rounded-xl border border-[#3a3a3a] bg-[#242424] p-4 shadow-2xl text-[12.5px]">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#8a8a8a] mb-1">Link</div>
          <div className="flex gap-2 mb-3">
            <input readOnly value={window.location.href} className="flex-1 min-w-0 bg-[#1c1c1c] border border-[#3a3a3a] rounded px-2 py-1 text-[11.5px] text-[#d8d8d8]" />
            <button onClick={copy} className="px-2 rounded border border-[#3a3a3a] hover:border-[#e93529]" title="Copy link">
              {copied ? <Check className="w-3.5 h-3.5 text-[#4f9d76]" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#8a8a8a] mb-1">Password</div>
          <p className="text-[#b4b4b4] mb-2">
            {share === undefined ? "Checking…"
              : share ? `Shared. Set${share.set_by ? ` by ${share.set_by}` : ""} on ${new Date(share.set_at).toLocaleDateString()}. Anyone with the link and the password can read and comment.`
              : "Not shared yet. Set a password and anyone outside Proxy with the link and the password can read the memo and comment."}
          </p>
          <div className="flex gap-2">
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={share ? "New password" : "Set a password"}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              className="flex-1 min-w-0 bg-[#1c1c1c] border border-[#3a3a3a] rounded px-2 py-1 text-[#f4f4f4] outline-none focus:border-[#e93529]" />
            <button onClick={save} className="px-3 rounded bg-[#e93529] hover:bg-[#f04f44] font-semibold" style={{ color: "#ffffff" }}>Save</button>
          </div>
          {share && <button onClick={remove} className="mt-2 text-[11.5px] text-[#8a8a8a] hover:text-[#e93529]">Remove password (stop sharing)</button>}
          {msg && <p className="mt-2 text-[11.5px] text-[#c8922f]">{msg}</p>}
        </div>
      )}
    </div>
  );
}

export default function IcMemoPage({ route, deals, dealsLoading, readOnly, publicView = false, viewerName = null }) {
  const { accounts } = useMsal();
  const userName = publicView ? viewerName : accounts?.[0]?.name || accounts?.[0]?.username || null;
  const userEmail = publicView ? null : accounts?.[0]?.username || null;

  const deal = useMemo(() => findMemoDeal(route, deals), [route, deals]);
  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (deal) document.title = `${dealTitle(deal)} · IC Memo · Proxy`;
  }, [deal]);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    if (!deal?.id) return undefined;
    loadUwVersions(deal.id).then((vs) => {
      if (!alive) return;
      setVersions(vs);
      const pinned = route?.v != null ? vs.find((v) => v.version === route.v) : null;
      const cur = pinned || vs.find((v) => v.is_current) || vs[vs.length - 1];
      setVersionId(cur ? cur.id : null);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [deal?.id, route?.v]);

  // Keep ?v in the address bar in step with the picker, so the link shared is
  // the version being looked at; the current version needs no ?v.
  const pick = (id) => {
    setVersionId(id);
    const v = versions.find((x) => x.id === id);
    const url = new URL(window.location.href);
    if (!v || v.is_current) url.searchParams.delete("v");
    else url.searchParams.set("v", String(v.version));
    window.history.replaceState(null, "", url.pathname + url.search);
  };

  let body;
  if (dealsLoading) body = note("Loading IC Memo…");
  else if (!deal) body = note("No deal matches this IC Memo link. The deal may have been renamed - open it from the pipeline and use its IC Memo button for a fresh link.");
  else if (!loaded) body = note("Loading IC Memo…");
  else body = (
    <Suspense fallback={note("Loading IC Memo…")}>
      <IcMemoTab deal={deal} versions={versions} versionId={versionId} readOnly={readOnly}
                 userName={userName} userEmail={userEmail} />
    </Suspense>
  );

  return (
    <PageFrame title={deal ? dealTitle(deal) : ""} right={deal && (
      <div className="flex items-center gap-2">
        {versions.length > 0 && <UwVersionSelect versions={versions} selectedId={versionId} onSelect={pick} onUpload={null} />}
        {!publicView && !readOnly && <SharePanel deal={deal} userName={userName} />}
      </div>
    )}>
      {body}
    </PageFrame>
  );
}

// Anyone not signed in to Proxy: the deal's password, then the memo,
// read-only with comments open (they give a name to comment under).
export function PublicIcMemo({ route }) {
  const { instance } = useMsal();
  const [stage, setStage] = useState("loading");   // loading | missing | unshared | locked | open
  const [list, setList] = useState([]);
  const [share, setShare] = useState(null);
  const [deal, setDeal] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Titles only, to find the deal the link names.
      const { data } = await fetchAllRows(() => supabase.from("deals").select("id, source_notes, address").order("id"));
      if (!alive) return;
      const rows = (data || []).map((r) => {
        let addresses;
        try { const p = JSON.parse(r.address); addresses = Array.isArray(p) ? p : [r.address]; } catch { addresses = [r.address]; }
        return { id: r.id, sourceNotes: r.source_notes || "", addresses };
      });
      setList(rows);
      const hit = findMemoDeal(route, rows);
      if (!hit) { setStage("missing"); return; }
      const s = await loadShare(hit.id);
      if (!alive) return;
      setShare(s);
      if (!s) { setStage("unshared"); return; }
      if (isUnlocked(hit.id, s)) { await openDeal(hit.id); return; }
      setStage("locked");
    })();
    return () => { alive = false; };
  }, [route]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openDeal(id) {
    const { data } = await supabase.from("deals").select("*").eq("id", id).single();
    if (data) { setDeal(dbToDeal(data)); setStage("open"); } else setStage("missing");
  }

  const unlock = async () => {
    const hit = findMemoDeal(route, list);
    if (!hit || busy) return;
    setBusy(true); setErr("");
    const ok = await checkPassword(share, pw);
    setBusy(false);
    if (!ok) { setErr("That password is not right."); return; }
    rememberUnlock(hit.id, share);
    await openDeal(hit.id);
  };

  if (stage === "open" && deal) {
    return <IcMemoPage route={route} deals={[deal]} dealsLoading={false} readOnly publicView />;
  }

  const hit = findMemoDeal(route, list);
  const signIn = () => instance.loginRedirect(loginRequest).catch(() => {});
  return (
    <PageFrame title={hit ? dealTitle(hit) : ""}>
      {stage === "loading" && note("Loading…")}
      {stage === "missing" && note("No deal matches this IC Memo link.")}
      {(stage === "unshared" || stage === "locked") && (
        <div className="max-w-sm mx-auto px-6 py-20">
          <div className="flex items-center gap-2 text-[#e93529] mb-3"><Lock className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">Protected memo</span></div>
          {stage === "unshared" ? (
            <p className="text-[13px] text-[#b4b4b4] leading-relaxed mb-6">This memo has not been shared outside Proxy.</p>
          ) : (
            <>
              <p className="text-[13px] text-[#b4b4b4] leading-relaxed mb-4">Enter the password you were given for this memo.</p>
              <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") unlock(); }}
                className="w-full bg-[#242424] border border-[#3a3a3a] rounded-md px-3 py-2 text-[14px] text-[#f4f4f4] outline-none focus:border-[#e93529] mb-3" />
              <button onClick={unlock} disabled={busy || !pw}
                className="w-full rounded-md bg-[#e93529] hover:bg-[#f04f44] disabled:opacity-60 py-2 text-[13px] font-semibold" style={{ color: "#ffffff" }}>
                {busy ? "Checking…" : "Open memo"}
              </button>
              {err && <p className="mt-3 text-[12px] text-[#f04f44]">{err}</p>}
            </>
          )}
          <button onClick={signIn} className="mt-6 text-[12px] text-[#8a8a8a] hover:text-[#f4f4f4]">ProximityParks team? Sign in with Microsoft</button>
        </div>
      )}
    </PageFrame>
  );
}
